import React, { useState, createContext, useContext, ReactNode, useEffect, useRef, useCallback } from 'react';

export enum ToastType {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
}

export enum DisplayMode {
  SINGLE = 'single',
  MULTIPLE = 'multiple',
}

export interface ToastNotification {
  id: string;
  title: string;
  text: string;
  type: ToastType;
  actionBinding?: () => void;
  buttonText?: string;
  duration?: number;
  createdAt: number;
  timerStartedAt?: number;
}

interface UseToastNotificationsReturn {
  readonly notifications: ToastNotification[];
  readonly displayMode: DisplayMode;
  showToast: (toast: Omit<ToastNotification, 'id' | 'createdAt'>) => string;
  hideToast: (id: string) => void;
  setDisplayMode: (mode: DisplayMode) => void;
  clearAll: () => void;
}

const ToastNotificationsContext = createContext<UseToastNotificationsReturn | undefined>(undefined);

interface ToastNotificationsProviderProps {
  children: ReactNode;
  defaultDuration?: number;
}

export const ToastNotificationsProvider: React.FC<ToastNotificationsProviderProps> = ({ children, defaultDuration = 5000 }) => {
  const [notifications, setNotifications] = useState<ToastNotification[]>([]);
  const [displayMode, setDisplayMode] = useState<DisplayMode>(DisplayMode.MULTIPLE);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const generateId = () => Date.now().toString() + Math.random().toString(36).substring(2, 11);

  const hideToast = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((toast) => toast.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const startTimer = useCallback(
    (toast: ToastNotification) => {
      if (toast.duration && toast.duration > 0) {
        const now = Date.now();

        // Update the toast to mark when its timer started
        setNotifications((prev) => prev.map((t) => (t.id === toast.id ? { ...t, timerStartedAt: now } : t)));

        const timer = setTimeout(() => {
          hideToast(toast.id);
        }, toast.duration);
        timersRef.current.set(toast.id, timer);
      }
    },
    [hideToast],
  );

  const stopTimer = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);

      // Clear the timer start time
      setNotifications((prev) => prev.map((t) => (t.id === id ? { ...t, timerStartedAt: undefined } : t)));
    }
  }, []);

  const showToast = (toast: Omit<ToastNotification, 'id' | 'createdAt'>): string => {
    const id = generateId();
    const newToast: ToastNotification = {
      ...toast,
      id,
      createdAt: Date.now(),
      duration: toast.duration ?? defaultDuration,
    };

    setNotifications((prev) => [...prev, newToast]);

    return id;
  };

  const clearAll = () => {
    setNotifications([]);
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
  };

  // Manage timers based on display mode
  useEffect(() => {
    if (displayMode === DisplayMode.MULTIPLE) {
      // In multiple mode, all notifications should have timers
      notifications.forEach((toast) => {
        if (!timersRef.current.has(toast.id)) {
          startTimer(toast);
        }
      });
    } else if (displayMode === DisplayMode.SINGLE) {
      // In single mode, only the first notification should have a timer
      notifications.forEach((toast, index) => {
        if (index === 0) {
          // Start timer for first toast if it doesn't have one
          if (!timersRef.current.has(toast.id)) {
            startTimer(toast);
          }
        } else {
          // Stop timer for all other toasts
          stopTimer(toast.id);
        }
      });
    }
  }, [notifications, displayMode, startTimer, stopTimer]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  return (
    <ToastNotificationsContext.Provider
      value={{
        notifications,
        displayMode,
        showToast,
        hideToast,
        setDisplayMode,
        clearAll,
      }}
    >
      {children}
    </ToastNotificationsContext.Provider>
  );
};

export const useToastNotifications = (): UseToastNotificationsReturn => {
  const context = useContext(ToastNotificationsContext);
  if (context === undefined) {
    throw new Error('useToastNotifications must be used within a ToastNotificationsProvider');
  }
  return context;
};
